import * as Sentry from '@sentry/react'
import * as _ from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { createContext } from 'react'

// eslint-disable-next-line import/no-cycle
import { getUser, updateUser } from '../services/user.service'
import { identify as analyticsIdentify } from '../utils/analytics'
import { getErrorMessage } from '../utils/helpers'

import { BaseStore } from './BaseStore'

const INITIAL_STATE = {
  accessToken: '',
  refreshToken: '',
  user: {},
  // WARP-567 — set while viewing as someone else; holds the *real* signed-in user.
  impersonator: null,
  loading: false,
  lastUpdated: null,
  error: null,
}

const TYPES = {
  accessToken: observable,
  refreshToken: observable,
  user: observable,
  impersonator: observable,
  loading: observable,
  error: observable,
  lastUpdated: observable,
  isAuthenticated: computed,
  isDealerUser: computed,
  isDealerAdmin: computed,
  isDealerService: computed,
  isWpcUser: computed,
  isWpcAdmin: computed,
  isWpcExecutive: computed,
  isWpcReadOnlyUser: computed,
  isImpersonating: computed,
  isReadOnlySession: computed,
  canImpersonate: computed,
  canEditProblemWithoutTimestamps: computed,
  hasAcceptedTermsAndConditions: computed,
  hasValidProfile: computed,
  shouldReceiveNotifications: computed,
}

const IN_APP_NOTIFICATIONS = [
  'inAppNewComment',
  'inAppNewAttachment',
  'inAppReadyForReview',
  'inAppBilling',
]

export class UserStore extends BaseStore {
  /** ACCESS */

  get hasValidProfile() {
    if (
      this.user &&
      (this.user.ssoUserProfile !== undefined || this.user.dealerUserProfile !== undefined)
    ) {
      return true
    }

    return false
  }

  get isAuthenticated() {
    return this.accessToken !== ''
  }

  /** ROLE SPECIFIC */

  get isDealerUser() {
    if (this.user && this.user.id) {
      return this.user.userType === 'Dealer User'
    }
    return null
  }

  get isDealerAdmin() {
    if (this.user && this.user.id) {
      return this.isDealerUser && this.user.dealerUserProfile?.role === 'Administrator'
    }
    return false
  }

  get isDealerService() {
    if (this.user && this.user.id) {
      return this.isDealerUser && this.user.dealerUserProfile?.role === 'Service'
    }
    return false
  }

  get isSelfManagedUser() {
    return this.isDealerUser && this.user.isSelfManaged === true
  }

  get isWpcUser() {
    if (this.user && this.user.id) {
      return this.user.userType === 'WPC User'
    }
    return false
  }

  get isWpcReadOnlyUser() {
    if (this.user && this.user.id) {
      return this.isWpcUser && this.user.ssoUserProfile?.role === 'Read Only'
    }
    return false
  }

  get isWpcAdmin() {
    if (this.user && this.user.id) {
      return this.isWpcUser && this.user.ssoUserProfile?.role === 'Administrator'
    }
    return false
  }

  get isWpcManager() {
    if (this.user && this.user.id) {
      return this.isWpcUser && this.user.ssoUserProfile?.role === 'Manager'
    }
    return false
  }

  get isWpcExecutive() {
    if (this.user && this.user.id) {
      return this.isWpcUser && this.user.ssoUserProfile?.role === 'Executive'
    }
    return false
  }

  /** IMPERSONATION (WARP-567) */

  /**
   * Is this session viewing as someone else? When true, `user` is the person being
   * viewed and `impersonator` is whoever actually signed in.
   */
  get isImpersonating() {
    return !!this.impersonator?.id
  }

  /**
   * Can this session change anything?
   *
   * Two different reasons a session is read-only: the WPC "Read Only" role, and an
   * active impersonation. Write affordances gate on this rather than on either source,
   * so a new read-only reason is picked up everywhere at once. The backend refuses the
   * write either way — this is what keeps the UI from offering it.
   */
  get isReadOnlySession() {
    return this.isWpcReadOnlyUser || this.isImpersonating
  }

  /**
   * May this user start an impersonation session? Backed by WPC Executive role or the
   * admin-granted `can_impersonate` flag; the backend is the authority either way.
   */
  get canImpersonate() {
    return this.isWpcExecutive || !!this.user?.canImpersonate
  }

  // TEMP: WARP-840 — backed by User.can_edit_problem_without_timestamps. Granted
  // manually via Django admin; surfaces as a read-only field on the user
  // serializer. Consumed by RepairOrderActionItem to lift state-based gating
  // on Problem edits. Remove once a permanent allowlist solution lands.
  get canEditProblemWithoutTimestamps() {
    return !!this.user?.canEditProblemWithoutTimestamps
  }

  /** GENERAL */

  get hasAcceptedTermsAndConditions() {
    if (this.user && this.user.id) {
      return !!this.user.acceptedTermsAndConditionsAt
    }
    return null
  }

  get shouldReceiveNotifications() {
    if (this.user && this.user.notificationSettings) {
      return _.some(
        IN_APP_NOTIFICATIONS,
        (notification) => this.user.notificationSettings[notification],
      )
    }

    return false
  }

  /**
   * Updates the stored user data.
   * @param {object} payload
   */
  updateUser = action(async (payload) => {
    this.loading = true
    this.error = null

    try {
      const currentUser = await updateUser({ ...user, ...payload })
      this.user = currentUser

      Sentry.setUser({
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        ...currentUser,
      })

      this.lastUpdated = new Date()
      this.loading = false
    } catch (err) {
      this.error = getErrorMessage(err)
      this.loading = false
    }
  })

  /**
   * Gets updated user data with the specified `id`.
   * @param {string} id
   */
  getUpdatedUser = action(async (id) => {
    this.loading = true
    this.error = null

    if (!(id || this.user?.id)) {
      console.error('No user ID specified.')
      return
    }

    try {
      const currentUser = await getUser(id || this.user.id, this.accessToken)
      if (currentUser) {
        this.user = currentUser

        // Re-derive from the API rather than trusting the persisted value: after a
        // reload the client holds only its tokens, and the server is the authority on
        // whether this request is impersonated.
        this.impersonator = currentUser.impersonator || null

        // WARP-567 — an impersonated session's errors belong to the support person, not
        // to the customer on screen. Sentry indexes and counts events by the user's
        // standard id/email fields, so those have to be the actor's; the viewed account
        // rides along as context rather than as the identity.
        const actor = this.impersonator || currentUser
        Sentry.setUser({
          ...actor,
          name: `${actor.firstName} ${actor.lastName}`,
          viewingAs: this.impersonator ? currentUser.email : null,
        })

        // Do not identify an impersonated session to product analytics. The events
        // belong to the support person, not to the customer, and identifying as the
        // customer would attribute one person's behavior to another.
        if (!this.isImpersonating) {
          analyticsIdentify(currentUser)
        }

        this.lastUpdated = new Date()
        this.loading = false
      } else {
        this.error = 'Could not retrieve user details.'
        this.loading = false
      }
    } catch (err) {
      this.error = getErrorMessage(err)
      this.loading = false
    }
  })

  setCurrentTokens = action(({ access, refresh }) => {
    this.accessToken = access
    this.refreshToken = refresh
  })

  setCurrentUser = action((user) => {
    this.user = {
      ...this.user,
      ...user,
    }
  })

  /**
   * WARP-567 — swap this session onto an impersonated identity.
   *
   * Replaces the whole `user` object rather than merging, so no field of the real
   * signed-in user survives onto the person being viewed. Both `impersonating` and
   * `impersonator` are full user payloads: PageRoutes force-logs-out any authenticated
   * session whose stored user has no profile (`hasValidProfile`), so a partial object
   * here ends the session instead of starting it.
   */
  setImpersonation = action(({ access, refresh, impersonating, impersonator }) => {
    this.accessToken = access
    this.refreshToken = refresh
    this.user = impersonating
    this.impersonator = impersonator
  })

  /**
   * WARP-567 — return this session to the real signed-in user.
   *
   * `user` comes from the server rather than from the copy held for the banner. Both
   * sides of a swap must be complete user payloads: PageRoutes force-logs-out any
   * authenticated session whose stored user has no profile (`hasValidProfile`), so
   * restoring a partial object would drop the user at the login page.
   */
  clearImpersonation = action(({ access, refresh, user: currentUser }) => {
    this.accessToken = access
    this.refreshToken = refresh
    this.user = currentUser || this.impersonator || {}
    this.impersonator = null
  })
}

export const user = new UserStore(INITIAL_STATE, TYPES)
export const UserStoreContext = createContext(user)
